import contextViewport from '@contexts/viewport'
import useHeaderTopics from '@hooks/useHeaderTopics'
import usePageMetadata from '@hooks/usePageMetadata'
import useScrollArrow from '@hooks/useScrollArrow'
import { mobileCSS } from '@measures/responsive'
import {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled, { css } from 'styled-components'
import ArrowFader from '../ArrowFader'
import SimpleLink from '../SimpleLink'
import { scrollToEnd, scrollToStart } from '../utils'

const { ViewportTypeContext, ViewportWidthContext } = contextViewport

const Container = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto 1px auto 1fr;
  align-items: center;
  padding-left: 12px;
`

const StyledTitle = styled.div`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
    },
  }) => css`
    font-weight: bold;
    font-size: 16px;
    line-height: 20px;
    text-transform: uppercase;
    color: ${brand_55};
    margin-right: 24px;
  `}
`

const Divider = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_60 },
      },
    },
  }) => css`
    background-color: ${grey_60};
    width: 1px;
    height: 18px;
  `}
`

interface LinksAndFaderWrapperProps {
  isScrollable: boolean
}

const LinksAndFaderWrapper = styled.div<LinksAndFaderWrapperProps>`
  ${({ isScrollable }) => css`
    position: relative;
    display: grid;
    grid-auto-flow: column;
    height: 100%;
    ${isScrollable &&
    css`
      padding-left: 30px;
      ${mobileCSS(css`
        padding-left: 20px;
      `)}
    `}
  `}
`

interface LinksWrapperProps {
  nrOfLinks: number
  isScrollable: boolean
}

const LinksWrapper = styled.div<LinksWrapperProps>`
  ${({ nrOfLinks, isScrollable }) => css`
    align-self: stretch;
    display: grid;
    grid-template-columns: repeat(${nrOfLinks}, max-content);
    grid-gap: 40px;

    ${mobileCSS(css`
      grid-gap: 24px;
    `)}
    justify-items: center;
    overflow-x: scroll;
    align-items: center;

    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    ::-webkit-scrollbar {
      display: none;
    }
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;

    ${isScrollable
      ? css`
          padding-right: 30px;
          ${mobileCSS(css`
            padding-right: 20px;
          `)}
        `
      : css`
          margin-left: 24px;
        `}
  `}
`

const FirstLevelSection: FunctionComponent = () => {
  const headerTopics = useHeaderTopics()
  const { title } = usePageMetadata()
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false)
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false)
  const [isScrollable, setIsScrollable] = useState<boolean>(false)
  const linksWrapperElement = useRef<HTMLDivElement>(null)
  const viewportType = useContext(ViewportTypeContext)
  const viewportWidth = useContext(ViewportWidthContext)

  useEffect(() => {
    if (linksWrapperElement?.current) {
      const isOverflowing =
        linksWrapperElement.current.clientWidth <
        linksWrapperElement.current.scrollWidth
      setShowRightArrow(isOverflowing)
      setShowLeftArrow(false)
      setIsScrollable(isOverflowing)
    }
  }, [viewportWidth, viewportType])

  useScrollArrow({
    elementRef: linksWrapperElement,
    setShowLeftArrow,
    setShowRightArrow,
  })

  return (
    <Container>
      <StyledTitle>{title}</StyledTitle>
      {headerTopics.length > 0 && (
        <>
          <Divider />
          <LinksAndFaderWrapper isScrollable={isScrollable}>
            {showLeftArrow && (
              <ArrowFader.Left
                scrollToStart={() =>
                  scrollToStart({ linksContainerRef: linksWrapperElement })
                }
              />
            )}
            <LinksWrapper
              ref={linksWrapperElement}
              nrOfLinks={headerTopics.length}
              isScrollable={isScrollable}>
              {headerTopics.map(({ text, link }) => (
                <SimpleLink key={text} link={link}>
                  {text}
                </SimpleLink>
              ))}
            </LinksWrapper>
          </LinksAndFaderWrapper>
        </>
      )}
      {showRightArrow && (
        <ArrowFader.Right
          scrollToEnd={() =>
            scrollToEnd({ linksContainerRef: linksWrapperElement })
          }
        />
      )}
    </Container>
  )
}

export default FirstLevelSection
